import { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { getTokenAndId } from "utils/helper/localStorage";
import { store } from "stores/store";

const host = API_DOMAIN === "" ? location.host : API_DOMAIN;

const BASE_URL = `${IS_PROXY ? "http" : "https"}://${host}`;

const getApiInfo = (params?: any) => {
  let token = "";
  let id = "";

  if (params) {
    if (Object.prototype.hasOwnProperty.call(params, "tokenKey"))
      token = params["tokenKey"];
    if (Object.prototype.hasOwnProperty.call(params, "userId"))
      id = params["userId"];
  }

  if (token === "" || id === "") {
    const tokenAndId = getTokenAndId();
    token = tokenAndId.token;
    id = tokenAndId.id;
  }

  return { token, id };
};

const fetcher = async (
  path: string,
  params: any,
  { token, id }: { token: string; id: string }
) => {
  const headers: Record<string, string> = {};

  if (token) {
    headers["x-nvs-token"] = token;
  }

  store.dispatch({ type: `api${path}_PENDING` });

  const response = await fetch(
    `${BASE_URL}/${API_PATH}${path}${
      params ? `?${new URLSearchParams(params)}` : ""
    }${id ? `&userId=${id}` : ""}`,
    { headers }
  );

  const json = await response.json();

  if (!response.ok) {
    const vsError = handleError({ status: response.status, data: json }, path);

    const error = {
      status: response.status,
      data: json,
      message: vsError.message
    };

    store.dispatch({ type: `api${path}_REJECTED`, payload: vsError });

    throw error;
  }

  store.dispatch({
    type: `api${path}_FULFILLED`,
    payload: json
  });

  return json;
};

// データ取得のフック
export const useRequest = <R>(path: string, params: any) => {
  const { token, id } = getApiInfo(params);

  const { data, error, isLoading } = useSWR<R>(
    [path, params, { token, id }],
    ([url, params, { token, id }]: [
      string,
      any,
      { token: string; id: string }
    ]) => fetcher(url, params, { token, id }),
    {
      dedupingInterval: 0,
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false
    }
  );

  return {
    data,
    error,
    isLoading
  };
};

class VsApiError implements Error {
  public name = "VsApiError";
  public body: any;
  public path = "";

  constructor(public message: string) {}

  toString() {
    return `${this.name} ${this.message}`;
  }
}

const handleError = (error: { status: number; data: any }, path: string) => {
  let message = "UNHANDLED_ERROR";

  switch (error.status) {
    case 400:
      message = "BAD_REQUEST";
      break;
    case 401:
      message = "UNAUTHORIZED";
      break;
    case 500:
      message = "INTERNAL_SERVER_ERROR";
      break;
    case 502:
      message = "BAD_GATEWAY";
      break;
    case 504:
      message = "GATEWAY_TIMEOUT";
      break;
    case 404:
      message = "NOT_FOUND";
      break;
  }

  const vsError = new VsApiError(message);
  if (error.data) {
    vsError.body = error.data;
  }
  vsError.path = path;

  return vsError;
};

type MutationKey = {
  path: string;
};

const mutationFetcher = async (
  key: MutationKey,
  {
    arg,
    method = "POST",
    headers: customHeaders = {}
  }: {
    arg?: any;
    method?: string;
    headers?: Record<string, string>;
  }
) => {
  const { path } = key;
  const { token, id } = getApiInfo(arg);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...customHeaders
  };

  if (token) {
    headers["x-nvs-token"] = token;
  }

  store.dispatch({ type: `api${path}_PENDING` });

  const requestParams = { ...arg, userId: id };

  const response = await fetch(
    `${BASE_URL}/${API_PATH}${path}${
      requestParams && method === "GET"
        ? `?${new URLSearchParams(requestParams)}`
        : ""
    }`,
    {
      method,
      headers,
      body: method === "POST" ? JSON.stringify(requestParams) : undefined
    }
  );

  const responseData = await response.json();

  if (!response.ok) {
    const vsError = handleError(
      { status: response.status, data: responseData },
      path
    );

    const error = {
      status: response.status,
      data: responseData,
      message: vsError.message
    };

    store.dispatch({ type: `api${path}_REJECTED`, payload: vsError });

    throw error;
  }

  store.dispatch({ type: `api${path}_FULFILLED`, payload: responseData });

  return responseData;
};

/**
 * トリガー型のデータ取得フック
 * @template R 取得データの型
 * @template P トリガーに渡すパラメータの型
 * @template E エラーの型
 */
export const useRequestMutation = <R = unknown, P = unknown, E = unknown>(
  path: string,
  method: "GET" | "POST" = "POST",
  options?: {
    onSuccess?: (data: R) => void;
    onError?: (error: E) => void;
  }
) => {
  const swrKey: MutationKey = {
    path
  };

  const { trigger, isMutating, data, error } = useSWRMutation<
    R,
    E,
    MutationKey,
    P
  >(
    swrKey,
    (key: MutationKey, { arg }: { arg: P }) =>
      mutationFetcher(key, { arg, method }),
    {
      onSuccess: options?.onSuccess,
      onError: options?.onError,
      populateCache: false,
      revalidate: false
    }
  );

  return {
    trigger,
    isMutating,
    data,
    error
  };
};

/**
 * 同じパスで複数回リクエストするためのフック
 */
export const useRequestMutationMultiple = <R = unknown, P = unknown>(
  path: string,
  method: "GET" | "POST" = "GET",
  options?: {
    onSuccess?: (data: R) => void;
    onError?: (error: any) => void;
  }
) => {
  const [data, setData] = useState<R[]>([]);
  const swrKey: MutationKey = {
    path
  };

  const { trigger: mutationTrigger } = useSWRMutation<R, any, any, any>(
    swrKey,
    (key: MutationKey, { arg }: { arg: P }) =>
      mutationFetcher(key, { arg, method }),
    {
      populateCache: false,
      revalidate: false
    }
  );

  const trigger = async (paramsArray: P[] = []): Promise<R[]> => {
    if (paramsArray.length === 0) {
      return [];
    }

    paramsArray.forEach(async params => {
      try {
        const result = await mutationTrigger(params);
        setData(prevData => [...prevData, result]);
        if (options?.onSuccess) {
          options.onSuccess(result);
        }
      } catch (error) {
        if (options?.onError) {
          options.onError(error);
        }
      }
    });

    return data;
  };

  const clearData = () => {
    setData([]);
  };

  return { data, trigger, clearData };
};
